import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FaCheck } from "react-icons/fa";
import { FiEdit} from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import TableSkeleton from "../../TableSkeleton";
import AccountExportOptions from "../../AccountExportOptions";
import { saveToCache, readFromCache } from "../../../services/pathologyCache";
import { queueOfflineCategory, getPendingCategories } from "../../../services/pathologyOutbox";
import { onSyncStatusChange } from "../../../services/pathologySyncEngine";
import Pagination from "../../Shared/Pagination";

const API_BASE = "https://api.muktihospital.com/api/pathology-category";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const PathologyCategoryModal = ({
  isOpen,
  onClose,
  onSave,
  editing,
  name,
  setName,
  loading,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 print-hidden">
      <div className="bg-white rounded-lg shadow-lg p-6 w-96">
        <h2 className="text-xl font-bold mb-4 text-gray-700">
          {editing ? "Edit Category" : "Add Category"}
        </h2>
        <input
          type="text"
          className="w-full mb-4 px-3 py-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color"
          placeholder="Category Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function PathologyCategoryList() {
  const [list, setList] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 10;

  const fetchList = useCallback(async () => {
    setLoading(true);

    // Helper: map a pending outbox category item to a row the UI can render.
    const mapPendingCategoryToRow = (item) => {
      const p = item.payload || {};
      return {
        id: `offline-${item.clientId}`,
        name: p.name || "",
        __offline: true,
      };
    };

    // Pending (offline-created) categories not yet synced — shown on top.
    let pendingRows = [];
    try {
      const pending = await getPendingCategories();
      pendingRows = pending.map(mapPendingCategoryToRow);
    } catch (e) {
      pendingRows = [];
    }

    try {
      const { data } = await axios.get(API_BASE, {
        headers: { "x-api-key": API_KEY },
      });
      const categories = Array.isArray(data.categories) ? data.categories : [];
      setList([...pendingRows, ...categories]);
      setTotalRecords((data.pagination?.totalItems || categories.length) + pendingRows.length);
      // Cache the SERVER list only (not the pending rows).
      saveToCache("pathology_categories", categories);
    } catch (err) {
      console.error("Fetch error:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      // Offline / server down: show cached categories + pending rows.
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        const cached = (await readFromCache("pathology_categories")) || [];
        const merged = [...pendingRows, ...cached];
        setList(merged);
        setTotalRecords(merged.length);
        if (merged.length > 0) {
          toast.info("Offline: showing cached categories.");
        }
      } else {
        setList(pendingRows);
        setTotalRecords(pendingRows.length);
        toast.error(err.response?.data?.error || "Failed to load categories");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Auto-refresh the category list when the sync engine finishes pushing
  // pending offline items. Mirrors the bill / test / agent list behaviour.
  useEffect(() => {
    const unsub = onSyncStatusChange((status) => {
      if (status && status.justSynced && status.justSynced > 0) {
        // Small delay so the server commit settles before we re-fetch.
        setTimeout(() => {
          fetchList();
        }, 1200);
      }
    });
    return () => unsub();
  }, [fetchList]);

  const filteredList = list.filter((item) =>
    item.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const currentData = filteredList.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(currentData.map((item) => item.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (categoryId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(categoryId)
        ? prevSelectedRows.filter((id) => id !== categoryId)
        : [...prevSelectedRows, categoryId]
    );
  };

  const openModal = (cat = null) => {
    setEditing(cat);
    setName(cat?.name || "");
    setModalOpen(true);
  };

  const handleSave = async () => {
  if (!name.trim()) {
    toast.warn("Category name is required");
    return;
  }
  const isDuplicate = list.some(
    (item) => item.name.toLowerCase() === name.trim().toLowerCase()
  );
  if (isDuplicate) {
    toast.warn("Category already exists!");
    return;
  }
  setLoading(true);
  try {
    if (editing) {
      await axios.put(
        `${API_BASE}/edit/${editing.id}`,
        { name },
        { headers: { "x-api-key": API_KEY } }
      );
      toast.success("Category updated successfully");
    } else {
      await axios.post(
        `${API_BASE}/add`,
        { name },
        { headers: { "x-api-key": API_KEY } }
      );
      toast.success("Category added successfully");
    }
    setModalOpen(false);
    setName("");
    fetchList();
  } catch (err) {
    console.error("Save error:", {
      message: err.message,
      response: err.response?.data,
      status: err.response?.status,
    });
    const isNetworkError =
      err.code === "ERR_NETWORK" || !navigator.onLine;
    // Offline + creating a new category -> queue it for later sync.
    // Edits stay online-only for now (matches bill/test/agent pattern).
    if (isNetworkError && !editing) {
      const clientId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const ok = await queueOfflineCategory({ name: name.trim() }, clientId);
      if (ok) {
        toast.success("Category saved offline. It will sync when you are online.");
        setModalOpen(false);
        setName("");
        fetchList();
        setLoading(false);
        return;
      }
    }
    toast.error(err.response?.data?.error || "Save failed");
  } finally {
    setLoading(false);
  }
};

  const handleDelete = async (categoryIds) => {
    try {
      if (Array.isArray(categoryIds)) {
        await Promise.all(
          categoryIds.map((id) =>
            axios.delete(`${API_BASE}/delete/${id}`, {
              headers: { "x-api-key": API_KEY },
            })
          )
        );
        toast.success(`${categoryIds.length} category(s) deleted successfully`);
      } else {
        await axios.delete(`${API_BASE}/delete/${categoryIds}`, {
          headers: { "x-api-key": API_KEY },
        });
        toast.success("Category deleted successfully");
      }
      setSelectedRows([]);
      fetchList();
    } catch (err) {
      console.error("Delete error:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      toast.error(err.response?.data?.error || "Delete failed");
    }
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = ["No.", "Name"];
  const dataMapper = (item, index, array) => {
    const serial = array.indexOf(item) + 1;
    return [serial, item.name || ""].join("\t");
  };

  const skeletonColumns = [
    { width: 20, height: 16, className: "print-hidden-in-print" }, // Checkbox
    { width: 30, height: 16 }, // No.
    { width: 150, height: 16 }, // Name
    { width: 80, height: 16, className: "print-hidden-in-print" }, // Actions
  ];

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Pathology Categories
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by category name"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
            <div className="flex items-center justify-between w-full gap-3">
              <button
                onClick={() => openModal()}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              >
                + Add Category
              </button>
              <AccountExportOptions
                  data={filteredList}
                  title="Pathology Category Master Database"
                  subTitle={`Total Active Categories: ${filteredList.length}`}
                  headers={headers}
                  dataMapper={(item, index) => dataMapper(item, index, filteredList)}
                  dropdownOpen={dropdownOpen}
                  toggleDropdown={() => setDropdownOpen(!dropdownOpen)}
                />
            </div>
          </div>
        </div>
        <div>
          <style>
            {`
              @media print {
                .print-hidden, .print-hidden-in-print {
                  display: none !important;
                }
                .print-all-rows {
                  display: table-row !important;
                }
                .print-table {
                  width: 100%;
                  border-collapse: collapse;
                }
                .print-table th, .print-table td {
                  border: 1px solid #000;
                  padding: 8px;
                  text-align: left;
                  font-size: 12px;
                }
                .print-table th {
                  background-color: #f0f0f0;
                  font-weight: bold;
                }
                .print-table tbody tr:nth-child(even) {
                  background-color: #f9f9f9;
                }
              }
            `}
          </style>
          <div className="overflow-x-auto print-wrapper">
            <table className="min-w-[1000px] w-full divide-y divide-gray-200 print-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left print-hidden-in-print">
                    <div className="relative left-3 -top-[2px]">
                      <input
                        type="checkbox"
                        id="agreement"
                        className="hidden peer print-hidden"
                        onChange={handleSelectAll}
                        checked={
                          currentData.length > 0 &&
                          currentData.every((item) =>
                            selectedRows.includes(item.id)
                          )
                        }
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label
                        htmlFor="agreement"
                        className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                      ></label>
                    </div>
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    No.
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Name
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton
                    rowCount={itemsPerPage}
                    columns={skeletonColumns}
                    baseColor="#e5e7eb"
                    highlightColor="#f3f4f6"
                  />
                ) : currentData.length === 0 ? (
                  <tr className="print-hidden">
                    <td
                      colSpan={4}
                      className="text-center py-4 capitalize font-inter font-normal text-sm text-M-heading-color"
                    >
                      No categories found.
                    </td>
                  </tr>
                ) : (
                  <>
                    {currentData.map((item, i) => {
                      const serialNumber =
                        (currentPage - 1) * itemsPerPage + i + 1;
                      return (
                        <tr
                          key={`normal-${item.id}`}
                          className="border-b hover:bg-gray-50 print-hidden"
                        >
                          <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                            <div className="relative left-3 -top-[1px]">
                              <input
                                type="checkbox"
                                id={`category-${i}`}
                                className="hidden peer print-hidden"
                                checked={selectedRows.includes(item.id)}
                                onChange={() => handleRowSelect(item.id)}
                              />
                              <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                                <FaCheck className="text-slate-100" />
                              </span>
                              <label
                                htmlFor={`category-${i}`}
                                className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                              ></label>
                            </div>
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {serialNumber}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {item.name || "N/A"}
                            {item.__offline && (
                              <span className="ml-2 inline-block bg-orange-50 text-orange-600 border border-orange-200 text-[10px] font-medium px-2 py-0.5 rounded-full align-middle">
                                Pending Sync
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-left space-x-2 print-hidden-in-print flex shrink-0">
                            {item.__offline ? (
                              <span className="text-xs text-gray-400 italic">
                                Waiting for sync
                              </span>
                            ) : (
                              <>
                            <button
                              onClick={() => openModal(item)}
                              className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                              title="Edit"
                            >
                              <FiEdit />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedRows([item.id]);
                                setDeleteModalOpen(true);
                              }}
                              className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                              title="Delete"
                            >
                              <RiDeleteBin6Line />
                            </button>
                            </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {Array.isArray(filteredList) && filteredList.length > 0 ? (
                      filteredList.map((item, i) => {
                        const serialNumber = i + 1;
                        return (
                          <tr
                            key={`print-${item.id}`}
                            className="border-b print-all-rows"
                            style={{ display: "none" }}
                          >
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3 print-hidden-in-print"></td>
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              {serialNumber}
                            </td>
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              {item.name || "N/A"}
                            </td>
                            <td className="p-3 text-left space-x-2 print-hidden-in-print"></td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr
                        className="print-all-rows"
                        style={{ display: "none" }}
                      >
                        <td
                          colSpan={3}
                          className="text-center py-4 capitalize font-inter font-normal text-sm text-M-heading-color"
                        >
                          No categories found.
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
          {/* Reusable Pagination */}
          <div className="print-hidden">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={filteredList.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      <PathologyCategoryModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
          editing={editing}
          name={name}
          setName={setName}
          loading={loading}
        />
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="category"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
        message={
          selectedRows.length === 1
            ? "Do you really want to delete this category?"
            : `Do you really want to delete ${selectedRows.length} categories?`
        }
      />
    </div>
  );
}
